"use client";

import { NotificationProps } from "@interfaces/UploadInterfaces";
import { createContext, ReactNode, useContext, useState } from "react";

type CsvType = {
  name: string;
  url: string | null;
};

type ReportType = {
  title: string[];
  transcript: string;
  url: string;
  sections: {
    title: string;
    content: string | string[];
  }[];
};

type UploadContextType = {
  videoURL: string | null;
  setVideoURL: (url: string | null) => void;
  csvUrls: CsvType[];
  setCsvUrls: (csvs: CsvType[]) => void;
  complete: boolean;
  setComplete: (value: boolean) => void;
  uploading: boolean;
  setUploading: (value: boolean) => void;
  notification: NotificationProps | null;
  setNotification: (notification: NotificationProps | null) => void;
  files: File[];
  setFiles: (files: File[]) => void;
  expandedIndex: number | null;
  setExpandedIndex: (index: number | null) => void;
  report: ReportType | null;
  setReport: (report: ReportType | null) => void;
};

const UploadContext = createContext<UploadContextType | undefined>(undefined);

/**
 * UploadProvider component that provides upload-related state and functions to its children via context.
 *
 * @param {ReactNode} [children] - The child components that will have access to the upload context.
 *
 * @returns {JSX.Element} The UploadContext.Provider component with the provided state and functions.
 *
 * @typedef {Object} NotificationProps - The type definition for notification properties.
 * @typedef {Object} UploadContextType - The type definition for the upload context value.
 * @property {string | null} videoURL - The URL of the uploaded video.
 * @property {Function} setVideoURL - Function to set the video URL.
 * @property {string[]} csvUrls - The list of CSV file URLs.
 * @property {Function} setCsvUrls - Function to set the list of CSV file URLs.
 * @property {File[]} files - The list of files to be uploaded.
 * @property {Function} setFiles - Function to set the list of files.
 * @property {NotificationProps | null} notification - The notification properties.
 * @property {Function} setNotification - Function to set the notification properties.
 * @property {boolean} uploading - The uploading state.
 * @property {Function} setUploading - Function to set the uploading state.
 * @property {boolean} complete - The upload completion state.
 * @property {Function} setComplete - Function to set the upload completion state.
 * @property {number | null} expandedIndex - The index of the expanded file.
 * @property {Function} setExpandedIndex - Function to set the expanded file index.
 * @property {ReportType | null} report - The generated report data.
 * @property {Function} setReport - Function to set the generated report data.
 */
export const UploadProvider = ({ children }: { children: ReactNode }) => {
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const [csvUrls, setCsvUrls] = useState<CsvType[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [notification, setNotification] = useState<NotificationProps | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [report, setReport] = useState<ReportType | null>(null);

  return (
    <UploadContext.Provider
      value={{
        videoURL,
        setVideoURL,
        csvUrls,
        setCsvUrls,
        files,
        setFiles,
        notification,
        setNotification,
        uploading,
        setUploading,
        complete,
        setComplete,
        expandedIndex,
        setExpandedIndex,
        report,
        setReport,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within a UploadProvider");
  }
  return context;
};
